// Global search — COMMAND rows: the ⌘K palette offers the shell keyboard
// registry's rows (HOME_AMBIENT_SKIN_SPEC §5: "The command palette (ws-w2)
// lists the same registry rows with keycaps" · DESIGN-WL-SHELL-KEYBOARD-MOTION-1).
//
// `rows` is `el.call('listShortcuts')` — { id, label, group, keycaps[], joiner,
// sequence, adopted, runnable, active }, `label` / `group` being bundle KEYS.
// PURE over data, so it unit-tests without a registry, a DOM or a network
// (globalSearch.js reads the registry and writes the state).
//
// WHAT IS OFFERED: a row whose command can run HERE, now — the registry's
// `runnable` (a `run` AND the row's own `when` for the live root, so
// "Customize dashboard" is not offered off the home board). Never an ADOPTED
// row (⌘K is HeaderSearch's, Enter is the focused card's — the registry lists
// them and runs neither), and never a key RANGE (1–9 opens the nth tile: the
// keystroke IS the argument, and a pick has none).
//
// KEYCAPS travel only while the keystroke works (`active` — the person's
// single-key switch, the workspace flag): a keycap for a dead key is a lie
// (AppKeycap's rule; the ? sheet drops those rows the same way). The row is
// still offered — a pick is a command, not a key (`runShortcut`).
//
// MATCHING: the translated label and the translated group ("go" offers every
// Go to row), in the session language and — with `fallbackLang` — in that
// language too, so a Georgian session still answers "keyboard". A translation
// that comes back AS its key (polyglot's missing-key echo) is never matched:
// every key shares its dotted prefix, and "sh" is not every command. Two
// buckets, as in searchAppResults.js: a field that STARTS with the query, then
// a field that contains it; ties keep the registry's own order.
//
// ROW SHAPE — double-keyed, like the app rows: the search-result contract
// (`_category` / `_id` / `_title` / `_subtitle`, grouped and indexed by
// globalSearch.js) plus the sheet-row contract (`label` / `group` / `keycaps`
// / `joiner`), which the palette row renders through the ? sheet's own row
// (components/NavSearchDropdown.js NavSearchCommandItem extends
// ShortcutsSheetRow), so a shortcut reads the same in both places. KEYS, never
// pre-resolved strings, so a language switch re-renders. `_shortcutId` is the
// marker activateNavSearchSelection routes to `runSearchCommand`; there is no
// `_href` — a command is not a place. `ariaKeys` names the keystroke to
// assistive technology (aria-keyshortcuts): a chord or one key; null for a
// sequence ("G then H" is two keystrokes, which the attribute cannot say) and
// for a row with no live keystroke.
//
// Self-contained on purpose (frank serializes every registered function
// ALONE): every helper lives inside the function.
export const commandSearchHits = (rows, query, opts) => {
  const q = String(query || '')
    .trim()
    .toLowerCase()
  if (!q || !Array.isArray(rows)) return []
  const options = opts || {}
  const translate = typeof options.translate === 'function' ? options.translate : (k) => k
  const fallbackLang = options.fallbackLang || null
  const limit = options.limit || 5
  const said = (key, lang) => {
    if (!key) return ''
    let v = ''
    try {
      v = lang ? translate(key, lang) : translate(key)
    } catch (_) {
      v = ''
    }
    return typeof v === 'string' && v && v !== key ? v.toLowerCase() : ''
  }
  const ARIA = { '⌘': 'Meta', Ctrl: 'Control', '⌥': 'Alt', Alt: 'Alt', '⇧': 'Shift', Esc: 'Escape', '↵': 'Enter' }
  const scored = []
  for (const r of rows) {
    if (!r || typeof r !== 'object' || !r.id || !r.label) continue
    if (!r.runnable || r.adopted || r.joiner === 'range') continue
    const fields = [said(r.label), said(r.group)]
    if (fallbackLang) fields.push(said(r.label, fallbackLang), said(r.group, fallbackLang))
    let rank = -1
    for (const f of fields) {
      if (!f) continue
      const at = f.indexOf(q)
      if (at < 0) continue
      const score = at === 0 ? 0 : 1
      if (rank < 0 || score < rank) rank = score
    }
    if (rank < 0) continue
    const keycaps = r.active && Array.isArray(r.keycaps) ? r.keycaps.map(String) : []
    const ariaKeys =
      keycaps.length && !r.sequence && r.joiner !== 'then'
        ? keycaps.map((cap) => ARIA[cap] || cap).join('+')
        : null
    scored.push({
      rank,
      order: scored.length,
      row: {
        _category: 'commands',
        _id: 'command:' + r.id,
        _title: r.label,
        _subtitle: r.group || '',
        _shortcutId: r.id,
        label: r.label,
        group: r.group || '',
        keycaps,
        joiner: r.joiner || null,
        ariaKeys
      }
    })
  }
  return scored
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .slice(0, limit)
    .map((s) => s.row)
}
